<script setup lang="ts">
import CompactTextMenu from "@/components/ui/inputs/compact/CompactTextMenu.vue";
import {
  isAnsweredValue,
  useCompactScope,
  useSectionFieldRegistration,
} from "@/composables/shared/useCompactScope";
import { computed, ref, useAttrs } from "vue";
import { useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import { formatForElementId, humanizeErrorMessage } from "@/utils/Formatters";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  name?: string;
  label?: string;
  placeholder?: string;
  transparent?: boolean;
  required?: boolean;
  waitingForBackend?: boolean;
}>();

const fieldRef = ref<null | { focus: () => null }>(null);
const { t } = useI18n();
const { value, errorMessage } = useField(() => props.name ?? "");

function focus() {
  fieldRef.value?.focus();
}

defineExpose({ focus });

const elementId = computed(() => formatForElementId(props.label || props.name || ""));

const attrs = useAttrs();
const { isCompact } = useCompactScope();

const compactLabel = computed(() => props.label ?? t(`generic.fields.${props.name}`));

const compactValue = computed(() => {
  const current = attrs.modelValue ?? attrs["model-value"] ?? value.value;
  return typeof current === "string" ? current : undefined;
});

useSectionFieldRegistration(
  computed(() => isAnsweredValue(compactValue.value)),
  computed(() => Boolean(props.name) && !!errorMessage.value)
);

const isAttrDisabled = computed(
  () => attrs.disabled !== undefined && attrs.disabled !== false && attrs.disabled !== "false"
);

const isAttrReadonly = computed(
  () => attrs.readonly !== undefined && attrs.readonly !== false && attrs.readonly !== "false"
);

const isCompactEditable = computed(
  () => Boolean(props.name) && !isAttrDisabled.value && !isAttrReadonly.value
);
</script>

<template>
  <compact-text-menu
    v-if="isCompact"
    :caption="compactLabel"
    :value="compactValue"
    :error="!!errorMessage"
    :editable="isCompactEditable"
  >
    <v-textarea
      v-model="value"
      variant="outlined"
      density="compact"
      :label="compactLabel"
      :placeholder="placeholder"
      persistent-placeholder
      rows="4"
      hide-details="auto"
    />
  </compact-text-menu>

  <v-textarea
    v-else
    ref="fieldRef"
    v-model="value"
    :class="{
      'v-input--transparent': props.transparent,
      'v-input--required': props.required,
      'v-input--waiting-for-backend': props.waitingForBackend,
    }"
    :bgColor="props.transparent ? 'transparent' : 'background-secondary'"
    :label="props.label ?? t(`generic.fields.${props.name}`)"
    :error-messages="
      humanizeErrorMessage(
        t,
        errorMessage,
        props.name,
        props.label ?? t(`generic.fields.${props.name}`)
      )
    "
    variant="outlined"
    :placeholder="placeholder"
    persistent-placeholder
    :data-test-elementid="elementId"
    data-testid="text-area-field"
    v-bind="$attrs"
  >
    <template
      v-for="(slotContent, slotName) in $slots"
      :key="slotName"
      v-slot:[slotName]
    >
      <component :is="slotContent" />
    </template>
  </v-textarea>
</template>

<style src="@/assets/styles/components/fields.scss" scoped lang="scss"></style>
